import { useEffect, useState } from "react";

import type { ConflitoAlocacao } from "@/modules/carga-trabalho";
import type { Profissional } from "@/modules/recursos";
import {
  JUSTIFICATIVA_GERAL_MAX,
  SIMULACAO_APLICAR_IGNORAR_LABEL,
  SIMULACAO_APLICAR_LABEL,
} from "@/modules/recursos/simulacao";

import { nomeDoPapel } from "../../hooks/nome-do-papel";
import { useFocoPrimeiroInvalido } from "../../hooks/use-foco-primeiro-invalido";
import { useRolarAteErro } from "../../hooks/use-rolar-ate-erro";
import {
  useIgnorarConflito,
  useRemanejarProfissional,
} from "../../hooks/use-acoes-conflito-profissional";
import { IGNORAR_JUSTIFICATIVA_REQUIRED_MESSAGE } from "../conflito-ignorar-dialog/conflito-ignorar-dialog.constants";
import type { SubstitutoStepErrors } from "./components/substituto-step";
import {
  IGNORAR_GUIDANCE,
  OBRA_REQUIRED_MESSAGE,
  PAPEL_FALLBACK,
  REMANEJAR_GUIDANCE,
  REMANEJAR_JUSTIFICATIVA_REQUIRED_MESSAGE,
  RESOLVER_CONFLITO_GUIDANCE,
  RESOLVER_CONFLITO_TITLE,
  SELECIONAR_CONFLITO_TITLE,
  SUBSTITUTO_REQUIRED_MESSAGE,
} from "./resolver-conflito.constants";
import {
  descreverSobreposicao,
  formatIntervalo,
  isConflitoValido,
  obraTitulo,
  substitutoIndisponivel,
} from "./resolver-conflito.utils";
import type {
  ResolverConflitoDialogProps,
  ResolverConflitoDialogViewModel,
  ResolverConflitoEtapa,
  ResolverConflitoPrimario,
  ResolverConflitoTratamento,
} from "./types-resolver-conflito";

const SEM_ERROS: SubstitutoStepErrors = { obra: null, substituto: null };

/**
 * Modal "Resolver Conflito de Alocação" (HU 2434 / US 2040). Com mais de um
 * conflito, o usuário escolhe qual tratar; depois decide entre Remanejar
 * Profissional (Selecionar Profissional → Revisão) e Ignorar Conflito. Nenhuma
 * requisição de escrita sai com campo obrigatório vazio.
 */
export function useResolverConflitoDialog({
  profissional,
  conflitos,
  carga,
  periodo,
  today,
  canceladaVisivel,
  onResolved,
  onClose,
  simulacao,
}: ResolverConflitoDialogProps): ResolverConflitoDialogViewModel {
  const [etapa, setEtapa] = useState<ResolverConflitoEtapa>(
    conflitos.length > 1 ? "conflito" : "proposta",
  );
  const [conflitoIndex, setConflitoIndex] = useState(0);
  const [tratamento, setTratamento] = useState<ResolverConflitoTratamento>("remanejar");
  const [alocacaoId, setAlocacaoId] = useState<string | null>(null);
  const [substituto, setSubstituto] = useState<Profissional | null>(null);
  const [search, setSearch] = useState("");
  const [stepErrors, setStepErrors] = useState<SubstitutoStepErrors>(SEM_ERROS);
  const [justificativa, setJustificativa] = useState("");
  const [justificativaObrigatoriaVisivel, setJustificativaObrigatoriaVisivel] = useState(false);
  const [justificativaServerError, setJustificativaServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { corpoRef, focarPrimeiroInvalido } = useFocoPrimeiroInvalido();
  const erroRef = useRolarAteErro(serverError);
  const { remanejar, isSubmitting } = useRemanejarProfissional();
  const { ignorar } = useIgnorarConflito();

  const candidato: ConflitoAlocacao | undefined = conflitos[conflitoIndex];
  const conflito = isConflitoValido(candidato) ? candidato : null;
  const alocacao = conflito
    ? ([conflito.a, conflito.b].find((item) => item.id === alocacaoId) ?? null)
    : null;

  const [papelResolvido, setPapelResolvido] = useState<{ id: string; nome: string } | null>(null);
  const papelObraId = alocacao?.papelObraId ?? null;

  // Resolve o nome do papel ao entrar na Revisão; sem id ou com a lista fora, fica "profissional".
  useEffect(() => {
    if (etapa !== "revisao" || !papelObraId) return;
    let cancelado = false;
    nomeDoPapel(papelObraId)
      .then((nome) => {
        if (!cancelado && nome) setPapelResolvido({ id: papelObraId, nome });
      })
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, [etapa, papelObraId]);

  const papelRotulo =
    papelResolvido && papelResolvido.id === papelObraId ? papelResolvido.nome : PAPEL_FALLBACK;

  const justificativaVazia = justificativa.trim() === "";

  function fechar() {
    if (!isSubmitting) onClose();
  }

  function avancarProposta() {
    setEtapa(tratamento === "remanejar" ? "substituto" : "ignorar");
  }

  function avancarSubstituto() {
    const errors: SubstitutoStepErrors = {
      obra: alocacao ? null : OBRA_REQUIRED_MESSAGE,
      substituto: substituto ? null : SUBSTITUTO_REQUIRED_MESSAGE,
    };
    setStepErrors(errors);
    if (errors.obra || errors.substituto) {
      focarPrimeiroInvalido();
      return;
    }
    setServerError(null);
    setEtapa("revisao");
  }

  function voltarParaSubstituto() {
    setServerError(null);
    setJustificativaObrigatoriaVisivel(false);
    setEtapa("substituto");
  }

  async function confirmarRemanejamento() {
    if (isSubmitting || !alocacao || !substituto) return;
    if (justificativaVazia) {
      setJustificativaObrigatoriaVisivel(true);
      focarPrimeiroInvalido();
      return;
    }
    // Com `simulacao`, nunca grava: sem conflito válido, não faz nada.
    if (simulacao) {
      if (!conflito) return;
      setServerError(null);
      const resultado = await simulacao.remanejar({
        conflito,
        alocacao,
        substituto,
        justificativa: justificativa.trim(),
      });
      if (resultado.ok) onResolved({ tipo: "remanejado" });
      else if (resultado.mensagem) setServerError(resultado.mensagem);
      return;
    }

    setJustificativaServerError(null);
    setServerError(null);
    const result = await remanejar({
      alocacaoId: alocacao.id,
      profissionalSubstitutoId: substituto.id,
      justificativa: justificativa.trim(),
      disponibilidadeVisualizada: substitutoIndisponivel(substituto)
        ? "INDISPONIVEL"
        : "DISPONIVEL",
    });
    if (result.ok) {
      onResolved({ tipo: "remanejado" });
      return;
    }
    if (result.kind === "unavailable") {
      // "Escolha outro substituto": volta à seleção (a etapa remonta e recarrega a lista com a
      // disponibilidade atual), sem substituto marcado; a obra e a justificativa ficam.
      setSubstituto(null);
      setSearch("");
      setJustificativaObrigatoriaVisivel(false);
      setEtapa("substituto");
      setServerError(result.message);
      return;
    }
    if (result.kind === "invalid") setJustificativaServerError(result.message);
    else setServerError(result.message);
  }

  const primario = primarioDaEtapa();

  function primarioDaEtapa(): ResolverConflitoPrimario | null {
    if (!conflito) return null;
    const botao = (rotulo: string, testId: string, acionar: () => void) => ({
      rotulo,
      testId,
      acionar,
      carregando: false,
      desabilitado: false,
    });
    switch (etapa) {
      case "conflito":
        return botao("Avançar", "resolver-conflito-avancar", () => setEtapa("proposta"));
      case "proposta": {
        const rotuloIgnorar = simulacao ? SIMULACAO_APLICAR_IGNORAR_LABEL : "Salvar";
        return botao(
          tratamento === "remanejar" ? "Avançar" : rotuloIgnorar,
          "resolver-conflito-avancar",
          avancarProposta,
        );
      }
      case "substituto":
        return botao("Próximo Passo", "resolver-conflito-proximo", avancarSubstituto);
      case "revisao":
        return {
          ...botao(
            simulacao ? SIMULACAO_APLICAR_LABEL : "Confirmar Mudança",
            "resolver-conflito-confirmar",
            () => void confirmarRemanejamento(),
          ),
          carregando: isSubmitting,
          desabilitado: Boolean(simulacao && justificativaVazia),
        };
      default:
        return null;
    }
  }

  const ignorarProps =
    etapa === "ignorar" && conflito
      ? {
          descricao: IGNORAR_GUIDANCE,
          mensagemObrigatoria: IGNORAR_JUSTIFICATIVA_REQUIRED_MESSAGE,
          onCancel: onClose,
          confirmLabel: simulacao ? SIMULACAO_APLICAR_LABEL : undefined,
          maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : undefined,
          onConfirm: async (texto: string) => {
            const alocacaoIds = [conflito.a.id, conflito.b.id];
            // Modo Simulação: a justificativa da ação segue no cenário (comentários da 2224 e 2221).
            if (simulacao) {
              const resultado = await simulacao.ignorar({ conflito, justificativa: texto });
              if (resultado.ok) onResolved({ tipo: "ignorado", alocacaoIds });
              else if (resultado.mensagem)
                return { kind: "error" as const, message: resultado.mensagem };
              return;
            }
            const result = await ignorar({
              profissionalId: profissional.id,
              alocacaoIds,
              justificativa: texto,
            });
            if (result.ok) {
              onResolved({ tipo: "ignorado", alocacaoIds });
              return;
            }
            return result;
          },
        }
      : null;

  const emWizard = etapa === "substituto" || etapa === "revisao";

  return {
    etapa,
    titulo: etapa === "conflito" ? SELECIONAR_CONFLITO_TITLE : RESOLVER_CONFLITO_TITLE,
    orientacao: emWizard ? REMANEJAR_GUIDANCE : RESOLVER_CONFLITO_GUIDANCE,
    simulando: Boolean(simulacao),
    isSubmitting,
    semConflito: !conflito,
    serverError,
    resumo: conflito
      ? {
          nome: profissional.nome,
          time: profissional.time,
          obras: [conflito.a, conflito.b].map((item) => ({
            key: item.id,
            titulo: obraTitulo(item.obra),
            intervalo: formatIntervalo(item),
          })),
          sobreposicao: descreverSobreposicao(conflito),
        }
      : null,
    escolha: {
      total: conflitos.length,
      opcoes: conflitos.map((item, index) => ({
        key: `${item.a.id}-${item.b.id}`,
        titulo: `${obraTitulo(item.a.obra)} × ${obraTitulo(item.b.obra)}`,
        descricao: `Alocado em 2 obras. Sobreposição: ${descreverSobreposicao(item)}`,
        marcada: index === conflitoIndex,
      })),
      escolher: (index) => {
        setConflitoIndex(index);
        setAlocacaoId(null);
      },
    },
    proposta: { tratamento, escolher: setTratamento },
    substituto:
      etapa === "substituto" && conflito
        ? {
            profissional,
            conflito,
            alocacaoId,
            substituto,
            search,
            errors: stepErrors,
            escolherObra: (id) => {
              setAlocacaoId(id);
              setStepErrors((atual) => ({ ...atual, obra: null }));
            },
            escolherSubstituto: (item) => {
              setSubstituto(item);
              setStepErrors((atual) => ({ ...atual, substituto: null }));
            },
            buscar: setSearch,
            limparBusca: () => setSearch(""),
          }
        : null,
    revisao:
      etapa === "revisao" && conflito && alocacao && substituto
        ? {
            profissional,
            alocacao,
            substituto,
            papelRotulo,
            carga,
            periodo,
            today,
            canceladaVisivel,
            simulando: Boolean(simulacao),
            disabled: isSubmitting,
            justificativa: {
              value: justificativa,
              // Justificativa vazia e recusa do serviço (400) aparecem no campo, como no Ignorar
              // (decisão do cliente de 27/10, que contraria o Figma 9463:57371).
              error:
                justificativaServerError ??
                (justificativaObrigatoriaVisivel ? REMANEJAR_JUSTIFICATIVA_REQUIRED_MESSAGE : null),
              maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : undefined,
              alterar: (valor) => {
                setJustificativa(valor);
                setJustificativaServerError(null);
                if (valor.trim() !== "") setJustificativaObrigatoriaVisivel(false);
              },
            },
          }
        : null,
    corpoRef,
    erroRef,
    ignorar: ignorarProps,
    rodape: { voltar: etapa === "revisao" && conflito ? voltarParaSubstituto : null, primario },
    fechar,
  };
}
